import type { Sequence } from "../types/Sequence"

/**
 * Maps a function over one or more sequences, yielding transformed values.
 * Processes multiple sequences sequentially (not in parallel).
 * Generator function for lazy evaluation.
 *
 * @param func - Function to apply to each item (receives item and index)
 * @param arrays - One or more sequences to map over
 * @returns Generator yielding transformed values
 *
 * @example
 * ```ts
 * import { Map } from "@pdfking/tfunc"
 *
 * const doubled = Map(x => x * 2, [1, 2, 3])
 * [...doubled] // [2, 4, 6]
 *
 * const withIndex = Map((x, i) => `${i}: ${x}`, ["a", "b", "c"])
 * [...withIndex] // ["0: a", "1: b", "2: c"]
 *
 * // Multiple sequences processed sequentially
 * const result = Map(x => x * 2, [1, 2], [3, 4])
 * [...result] // [2, 4, 6, 8]
 * ```
 */
export const Map = function* <T, N>(
  func: (item: T, index: number) => N,
  ...arrays: Sequence<T>[]
): Iterable<N> {
  for (const array of arrays) {
    let index = 0
    for (const item of array) {
      yield func(item, index++)
    }
  }
}
